import { Component, OnInit, inject } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import {
  AlertComponent,
  ButtonComponent,
  MbsPopupType,
  MbsSize,
  ModalService,
  SidepanelBase,
  SidepanelComponent,
  SwitcherComponent
} from 'mbs-ui-kit';
import { TFARequireModalComponent } from 'mbs-ui-shared@components';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { Administrator } from 'mbs-ui-shared@models';
import { DisablePasswordRecoveryModalComponent } from 'mbs-ui-shared@modules/password-recovery/modals/disable-password-recovery-modal/disable-password-recovery-modal.component';
import { EnablePasswordRecoveryWizardComponent } from 'mbs-ui-shared@modules/password-recovery/modals/enable-password-recovery-wizard/enable-password-recovery-wizard.component';
import { ProviderService } from 'mbs-ui-shared@services/provider.service';
import { mediumDateWithTimeMoment } from 'mbs-ui-shared@utils/date';
import { of } from 'rxjs';

import { DateTimeFormatComponent } from 'mbs-ui-shared@components/date-time-format/date-time-format.component';

import { I18NextPipe } from 'angular-i18next';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';
import { CanPipe } from 'libs/ability/src/lib/ability.pipe';
import { catchError } from 'rxjs/operators';

@UntilDestroy()
@Component({
  templateUrl: './sidepanel-password-recovery.component.html',
  styleUrls: ['./sidepanel-password-recovery.component.scss'],
  imports: [
    AlertComponent,
    ButtonComponent,
    CommonModule,
    DateTimeFormatComponent,
    I18NextPipe,
    ReactiveFormsModule,
    SidepanelComponent,
    SwitcherComponent,
    CanPipe
  ]
})
export class SidepanelPasswordRecoveryComponent extends SidepanelBase implements OnInit {
  private auth = inject(AuthFacade);
  private providerService = inject(ProviderService);
  private modalService = inject(ModalService);
  private ability = inject(AbilityService);

  public readonly alertType = MbsPopupType;

  public isDataLoaded = false;
  public showErrorAlert = false;

  public dateFormat = mediumDateWithTimeMoment;

  public dateCreated: string | number | Date = new Date();

  public form = new UntypedFormGroup({
    switcher: new FormControl(false)
  });

  public switcher = this.form.get('switcher');

  constructor() {
    super();
    this.auth.currentUser$.pipe(untilDestroyed(this)).subscribe((user: Administrator) => {
      this.updateSwitcher(user.ProviderInfo.PasswordRecoveryEnabled);
    });
  }

  ngOnInit(): void {
    this.switcher.valueChanges.pipe(untilDestroyed(this)).subscribe(this.handleSwitch);
    this.refreshRecoveryStatus();
  }

  handleSwitch = (value: boolean): void => {
    if (this.ability.cannot('read', '2FAEnabled')) {
      this.modalService
        .openCustom(TFARequireModalComponent, {
          size: MbsSize.sm,
          data: {
            actionName: 'Password Recovery'
          }
        })
        .catch(() => {
          this.updateSwitcher(!value);
        });
      return;
    }

    if (value) {
      this.showEnableModal()
        .then(() => {
          this.updateSwitcher(true);
          this.refreshRecoveryStatus();
        })
        .catch(() => {
          this.updateSwitcher(false);
        });
    } else {
      this.showDisableModal()
        .then(() => {
          this.updateSwitcher(false);
          this.refreshRecoveryStatus();
        })
        .catch(() => this.updateSwitcher(true));
    }
  };

  showEnableModal(): Promise<any> {
    return this.modalService.openRef(EnablePasswordRecoveryWizardComponent, { size: MbsSize.sm, isCustom: true }).result;
  }

  showDisableModal(): Promise<any> {
    return this.modalService.openRef(DisablePasswordRecoveryModalComponent, { size: MbsSize.sm, isCustom: true }).result;
  }

  updateSwitcher = (value): void => {
    this.switcher.setValue(value, { onlySelf: true, emitEvent: false });
  };

  refreshRecoveryStatus(): void {
    this.providerService
      .getPasswordRecoveryState()
      .pipe(
        untilDestroyed(this),
        catchError(() => of(null))
      )
      .subscribe({
        next: (data) => {
          this.updateSwitcher(data.enabled);
          data?.keyDate && (this.dateCreated = data.keyDate);
        },
        error: (err) => {
          if (err.status === 500 && err.error.type === 'https://mspbackups.com/errors/mbs/4123') {
            this.showErrorAlert = true;
          }
        },
        complete: () => {
          this.isDataLoaded = true;
        }
      });
  }
}
